import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const isInvalidTokenError = (error) => (
  error?.response?.status === 401
  || error?.response?.data?.code === 'token_not_valid'
);

const OwnerDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [ownerProfile, setOwnerProfile] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inviteForm, setInviteForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
  });
  const [ownerMessage, setOwnerMessage] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const selectedWorker = useMemo(
    () => workers.find((worker) => worker.id === selectedWorkerId) || null,
    [workers, selectedWorkerId],
  );

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    window.dispatchEvent(new Event('auth-changed'));
    onLogout?.();
    navigate('/login', { replace: true });
  };

  const authorizedRequest = async (requestFactory) => {
    const activeToken = localStorage.getItem('token');
    try {
      return await requestFactory(activeToken);
    } catch (error) {
      if (!isInvalidTokenError(error)) throw error;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        clearSession();
        throw error;
      }

      try {
        const refreshResponse = await axios.post(apiUrl('token/refresh/'), { refresh: refreshToken });
        const refreshedToken = refreshResponse?.data?.access;
        if (!refreshedToken) throw error;
        localStorage.setItem('token', refreshedToken);
        window.dispatchEvent(new Event('auth-changed'));
        return await requestFactory(refreshedToken);
      } catch (refreshError) {
        clearSession();
        throw refreshError;
      }
    }
  };

  const fetchWorkforceData = async () => {
    if (!localStorage.getItem('token')) {
      clearSession();
      return;
    }
    try {
      setLoading(true);
      const [meRes, workersRes, clientsRes, actionsRes] = await authorizedRequest(
        (accessToken) => Promise.all([
          axios.get(apiUrl('userprofiles/me/'), { headers: { Authorization: `Bearer ${accessToken}` } }),
          axios.get(apiUrl('userprofiles/owner/workforce/'), { headers: { Authorization: `Bearer ${accessToken}` } }),
          axios.get(apiUrl('userprofiles/clients/'), { headers: { Authorization: `Bearer ${accessToken}` } }),
          axios.get(apiUrl('userprofiles/owner/worker-actions/'), { headers: { Authorization: `Bearer ${accessToken}` } }),
        ]),
      );

      setOwnerProfile(meRes.data || null);
      setWorkers(workersRes.data || []);
      setClients(clientsRes.data || []);
      setActions(actionsRes.data || []);
      setSelectedWorkerId((prev) => prev || workersRes.data?.[0]?.id || null);
      setFeedback({ type: '', message: '' });
    } catch (error) {
      if (isInvalidTokenError(error)) return;
      const apiError = error?.response?.data?.error;
      setFeedback({
        type: 'error',
        message: apiError || 'Unable to load workforce data.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkforceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleInviteWorker = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await authorizedRequest((accessToken) => axios.post(
        apiUrl('userprofiles/owner/workers/invite/'), inviteForm,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      ));
      setInviteForm({ username: '', email: '', first_name: '', last_name: '' });
      setFeedback({ type: 'success', message: 'Worker invited and created successfully.' });
      await fetchWorkforceData();
    } catch (error) {
      if (isInvalidTokenError(error)) return;
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to invite worker.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleElevateClient = async () => {
    if (!selectedClientId) return;
    try {
      setSubmitting(true);
      await authorizedRequest((accessToken) => axios.post(
        apiUrl('userprofiles/owner/workers/elevate-client/'),
        { client_profile_id: selectedClientId },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      ));
      setSelectedClientId('');
      setFeedback({ type: 'success', message: 'Client elevated to worker.' });
      await fetchWorkforceData();
    } catch (error) {
      if (isInvalidTokenError(error)) return;
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to elevate client.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleWorkerActive = async (worker) => {
    try {
      setSubmitting(true);
      if (worker?.user?.is_active) {
        await authorizedRequest((accessToken) => axios.post(
          apiUrl(`userprofiles/owner/workers/${worker.id}/deactivate/`),
          { confirm_deactivate: true, reason: 'Owner lifecycle action' },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        ));
        setFeedback({ type: 'success', message: 'Worker deactivated.' });
      } else {
        await authorizedRequest((accessToken) => axios.post(
          apiUrl(`userprofiles/owner/workers/${worker.id}/reactivate/`),
          { reason: 'Owner lifecycle action' },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        ));
        setFeedback({ type: 'success', message: 'Worker reactivated.' });
      }
      await fetchWorkforceData();
    } catch (error) {
      if (isInvalidTokenError(error)) return;
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to update worker status.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedWorker || !ownerMessage.trim()) return;
    try {
      setSubmitting(true);
      await authorizedRequest((accessToken) => axios.post(
        apiUrl(`userprofiles/owner/workers/${selectedWorker.id}/message/`),
        { message: ownerMessage.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      ));
      setOwnerMessage('');
      setFeedback({ type: 'success', message: 'Message sent to worker.' });
      await fetchWorkforceData();
    } catch (error) {
      if (isInvalidTokenError(error)) return;
      setFeedback({
        type: 'error',
        message: error?.response?.data?.error || 'Unable to send message.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F6F4F8] p-6">
        <InlineFeedback type="error" message="Please log in to access owner operations." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#4B2A56] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">Owner Workforce Ops</h1>
              <p className="mt-2 text-sm text-purple-100">
                Company: {ownerProfile?.company?.name || 'Unassigned'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/calendar')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Calendar
              </button>
              <button
                type="button"
                onClick={() => navigate('/myBookings')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Bookings
              </button>
              <button
                type="button"
                onClick={() => navigate('/my-services')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Manage Services
              </button>
              <button
                type="button"
                onClick={() => navigate('/worker-availability')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Manage My Availability
              </button>
              <button
                type="button"
                onClick={() => navigate('/owner-company-page')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Company Page
              </button>
              <button
                type="button"
                onClick={() => navigate('/owner-insights')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25"
              >
                Insights
              </button>
              <button
                type="button"
                onClick={clearSession}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {feedback.message ? <InlineFeedback type={feedback.type || 'info'} message={feedback.message} /> : null}
        {loading ? <InlineFeedback type="info" message="Loading workforce operations..." /> : null}

        <section className="grid gap-4 lg:grid-cols-2">
          <form onSubmit={handleInviteWorker} className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-[#4B2A56]">Invite / Add Worker</h2>
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Username"
              value={inviteForm.username}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, username: event.target.value }))}
              required
            />
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              type="email"
              placeholder="Email"
              value={inviteForm.email}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
              required
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input
                className="rounded border px-3 py-2 text-sm"
                placeholder="First name"
                value={inviteForm.first_name}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, first_name: event.target.value }))}
              />
              <input
                className="rounded border px-3 py-2 text-sm"
                placeholder="Last name"
                value={inviteForm.last_name}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, last_name: event.target.value }))}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-[#2F855A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#276749]"
            >
              Create Worker
            </button>
          </form>

          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-[#4B2A56]">Elevate Existing Client</h2>
            <select
              value={selectedClientId}
              onChange={(event) => setSelectedClientId(event.target.value)}
              className="w-full rounded border px-3 py-2 text-sm"
            >
              <option value="">Select client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.user?.username || `Client #${client.id}`}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={submitting || !selectedClientId}
              onClick={handleElevateClient}
              className="rounded bg-[#304A8A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#263c70]"
            >
              Elevate to Worker
            </button>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl bg-white p-5 shadow-sm lg:col-span-2">
            <h2 className="mb-3 text-lg font-semibold text-[#4B2A56]">Worker Directory</h2>
            <ul className="space-y-3">
              {workers.map((worker) => (
                <li
                  key={worker.id}
                  className={`rounded-lg border p-3 ${selectedWorkerId === worker.id ? 'border-[#8A4AA3] bg-[#F7F1FA]' : 'border-gray-200'}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedWorkerId(worker.id)}
                      className="text-left"
                    >
                      <p className="font-semibold text-[#4B2A56]">{worker.user?.username || `Worker #${worker.id}`}</p>
                      <p className="text-xs text-gray-600">
                        Status: {worker.user?.is_active ? 'active' : 'inactive'} •
                        {' '}Upcoming bookings: {worker.visibility?.upcoming_booking_count || 0} •
                        {' '}Upcoming schedule days: {worker.visibility?.upcoming_schedule_days || 0}
                      </p>
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => handleToggleWorkerActive(worker)}
                      className={`rounded px-3 py-1.5 text-xs font-semibold text-white ${worker.user?.is_active ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    >
                      {worker.user?.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-[#4B2A56]">Message Worker</h2>
            <p className="text-xs text-gray-600">
              Selected: {selectedWorker?.user?.username || 'None'}
            </p>
            <textarea
              value={ownerMessage}
              onChange={(event) => setOwnerMessage(event.target.value)}
              className="w-full rounded border p-2 text-sm"
              rows={5}
              placeholder="Send a direct update to this worker..."
            />
            <button
              type="button"
              disabled={submitting || !selectedWorker || !ownerMessage.trim()}
              onClick={handleSendMessage}
              className="rounded bg-[#1D4ED8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1E40AF]"
            >
              Send Message
            </button>
          </div>
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold text-[#4B2A56]">Worker Management Audit</h2>
          {actions.length === 0 ? (
            <p className="text-sm text-gray-500">No worker actions recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {actions.map((action) => (
                <li key={action.id} className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p>
                    <strong>{action.action}</strong> • {action.target_worker_username || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-600">
                    By {action.actor_username || 'unknown'} at {action.created_at}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default OwnerDashboard;
